<template>
  <section class="category-section">
    <div class="category-container">
      <!-- Section Header -->
      <div class="category-header">
        <div class="category-header__content">
          <div class="category-header__accent"></div>
          <span class="category-header__badge">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            {{ $t('home.categories.label') || 'Our Inventory' }}
          </span>
          <h2 class="category-header__title">{{ $t('home.categories.title') }}</h2>
          <p class="category-header__subtitle">{{ $t('home.categories.subtitle') }}</p>
        </div>

        <NuxtLink to="/machines" class="category-header__cta">
          <span>{{ $t('home.categories.viewAll') }}</span>
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </NuxtLink>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="category-grid">
        <div v-for="i in 8" :key="i" class="category-card category-card--loading">
          <div class="category-card__skeleton-icon"></div>
          <div class="category-card__skeleton-text"></div>
          <div class="category-card__skeleton-count"></div>
        </div>
      </div>

      <!-- Categories Grid -->
      <div v-else-if="categories.length > 0" class="category-grid">
        <NuxtLink
          v-for="(category, index) in categories"
          :key="category.id"
          :to="`/machines?category=${category.id}`"
          class="category-card"
          :style="{ '--delay': `${index * 50}ms` }"
        >
          <!-- Decorative Background -->
          <div class="category-card__bg"></div>

          <!-- Icon Container -->
          <div class="category-card__icon">
            <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="getIconPath(category.name)" />
            </svg>
          </div>

          <!-- Category Name -->
          <h3 class="category-card__name">{{ category.name }}</h3>

          <!-- Machine Count -->
          <div class="category-card__count">
            <span class="category-card__count-number">{{ category.machines_count || 0 }}</span>
            <span class="category-card__count-label">{{ $t('home.categories.units') || 'Units' }}</span>
          </div>

          <!-- Arrow Indicator -->
          <div class="category-card__arrow">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </div>
        </NuxtLink>
      </div>

      <!-- Empty State -->
      <div v-else class="category-empty">
        <div class="category-empty__icon">
          <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        </div>
        <p class="category-empty__text">{{ $t('home.categories.noCategories') || 'No categories found' }}</p>
      </div>

    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const categories = ref([]);
const loading = ref(true);

const fetchCategories = async () => {
  try {
    loading.value = true;
    const config = useRuntimeConfig();
    const apiBase = config.public?.apiBase || '/api/v1';

    const response = await $fetch(`${apiBase}/categories`, {
      query: {
        with_machines_count: true,
        limit: 8
      }
    });

    categories.value = response.data || response || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    categories.value = [];
  } finally {
    loading.value = false;
  }
};

const getIconPath = (name) => {
  if (!name) return 'M4 6h16M4 10h16M4 14h16M4 18h16';

  const lowerName = name.toLowerCase();

  if (lowerName.includes('press') || lowerName.includes('printing') || lowerName.includes('heidelberg')) {
    return 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z';
  } else if (lowerName.includes('cut') || lowerName.includes('guillotine')) {
    return 'M14.121 14.121l4.879 4.879 1.414-1.414-4.879-4.879m-1.414 1.414l-4.879-4.879 1.414-1.414 4.879 4.879m-1.414-0.001l-2.828-2.828-1.414 1.414 2.828 2.828';
  } else if (lowerName.includes('bind') || lowerName.includes('book')) {
    return 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253';
  } else if (lowerName.includes('finish') || lowerName.includes('fold')) {
    return 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10';
  } else if (lowerName.includes('digital') || lowerName.includes('plotter')) {
    return 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z';
  }

  return 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z';
};

onMounted(() => {
  fetchCategories();
});
</script>

<style scoped>
/* Section Layout */
.category-section {
  padding: 80px 0;
  background: linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  position: relative;
  overflow: hidden;
}

.category-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.3), transparent);
}

.category-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
}

/* Header Styles */
.category-header {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 48px;
}

@media (min-width: 768px) {
  .category-header {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
}

.category-header__content {
  max-width: 600px;
}

.category-header__accent {
  width: 48px;
  height: 4px;
  background: linear-gradient(90deg, #0f172a 0%, #334155 100%);
  border-radius: 2px;
  margin-bottom: 16px;
}

.category-header__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(15, 23, 42, 0.08);
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  border-radius: 20px;
  margin-bottom: 16px;
  letter-spacing: 0.02em;
}

.category-header__title {
  font-size: 36px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 12px 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.category-header__subtitle {
  font-size: 18px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
}

.category-header__cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: #ffffff;
  color: #0f172a;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
}

.category-header__cta:hover {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(15, 23, 42, 0.15);
}

/* Grid Layout */
.category-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (min-width: 640px) {
  .category-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
}

@media (min-width: 1024px) {
  .category-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
}

/* Card Styles */
.category-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 28px 24px;
  background: #ffffff;
  border-radius: 20px;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(226, 232, 240, 0.8);
  overflow: hidden;
  animation: fadeInUp 0.5s ease forwards;
  animation-delay: var(--delay, 0ms);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.category-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.12);
  border-color: #3b82f6;
}

.category-card__bg {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.category-card:hover .category-card__bg {
  opacity: 1;
}

.category-card--loading {
  pointer-events: none;
  min-height: 180px;
}

.category-card__skeleton-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 16px;
}

.category-card__skeleton-text {
  width: 70%;
  height: 20px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 12px;
}

.category-card__skeleton-count {
  width: 40%;
  height: 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Icon */
.category-card__icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  color: #3b82f6;
  margin-bottom: 20px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e2e8f0;
}

.category-card:hover .category-card__icon {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #ffffff;
  border-color: transparent;
  transform: scale(1.05);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.25);
}

/* Name */
.category-card__name {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 16px 0;
  line-height: 1.3;
  transition: color 0.3s ease;
}

.category-card:hover .category-card__name {
  color: #1d4ed8;
}

/* Count */
.category-card__count {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

.category-card__count-number {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
}

.category-card__count-label {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* Arrow */
.category-card__arrow {
  position: absolute;
  bottom: 24px;
  right: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border-radius: 50%;
  color: #94a3b8;
  opacity: 0;
  transform: translateX(-10px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.category-card:hover .category-card__arrow {
  opacity: 1;
  transform: translateX(0);
  background: #2563eb;
  color: #ffffff;
}

/* Empty State */
.category-empty {
  text-align: center;
  padding: 80px 40px;
  background: #ffffff;
  border-radius: 24px;
  border: 2px dashed #e2e8f0;
}

.category-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f8fafc;
  border-radius: 50%;
  margin-bottom: 24px;
  color: #cbd5e1;
}

.category-empty__text {
  font-size: 16px;
  color: #64748b;
  font-weight: 500;
  margin: 0;
}

/* Responsive */
@media (max-width: 640px) {
  .category-section {
    padding: 60px 0;
  }

  .category-header__title {
    font-size: 28px;
  }

  .category-header__subtitle {
    font-size: 16px;
  }

  .category-card {
    padding: 20px;
  }

  .category-card__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    margin-bottom: 16px;
  }

  .category-card__name {
    font-size: 15px;
  }

  .category-card__count-number {
    font-size: 20px;
  }
}
</style>
